import React, { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

interface Props {
  leftChild: ReactNode;
  rightChild: ReactNode;
  /** If false, only the left pane is visible. */
  splitEnabled: boolean;
  /** Initial split fraction 0..1 for the left pane (default 0.55). */
  initialSplit?: number;
  /** Minimum fraction for either pane (default 0.25). */
  minSplit?: number;
  /** LocalStorage key used to persist the divider position. */
  storageKey?: string;
}

export const SplitView: React.FC<Props> = ({
  leftChild, rightChild, splitEnabled,
  initialSplit = 0.55, minSplit = 0.25, storageKey = 'efb_splitview_fraction',
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const [fraction, setFraction] = useState<number>(() => {
    try {
      const saved = parseFloat(localStorage.getItem(storageKey) ?? '');
      return Number.isFinite(saved) && saved >= minSplit && saved <= 1 - minSplit ? saved : initialSplit;
    } catch { return initialSplit; }
  });

  useEffect(() => {
    try { localStorage.setItem(storageKey, String(fraction)); } catch { /* ok */ }
  }, [fraction, storageKey]);

  const onDividerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!draggingRef.current || !rootRef.current) return;
      const rect = rootRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const f = Math.max(minSplit, Math.min(1 - minSplit, x / rect.width));
      setFraction(f);
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [minSplit]);

  const leftFlex = splitEnabled ? fraction : 1;

  return (
    <div
      ref={rootRef}
      style={{ position: 'relative', display: 'flex', width: '100%', height: '100%', overflow: 'hidden' }}
    >
      <div style={{ flex: leftFlex, minWidth: 0, height: '100%', position: 'relative' }}>
        {leftChild}
      </div>
      {splitEnabled && (
        <>
          <div
            onMouseDown={onDividerMouseDown}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize 3D pane"
            style={{
              width: 6,
              cursor: 'col-resize',
              background: 'var(--border, #334155)',
              zIndex: 10,
            }}
          />
          <div style={{ flex: 1 - fraction, minWidth: 0, height: '100%', position: 'relative' }}>
            {rightChild}
          </div>
        </>
      )}
    </div>
  );
};
